import React, { useState, useEffect, useMemo } from 'react';
import { RefreshCw, ShieldCheck, Search, AlertTriangle, Layers, UserCog, ChevronRight } from 'lucide-react';
import { SlidersHorizontal } from 'lucide-react';
import AdminAcessosMatriz from './AdminAcessosMatriz';

// ---------------------------------------------------------------------------
// MAPA DE ACESSO — quem alcança o quê.
//
// A permissão do portal está escrita em ~500 pontos do backend, espalhados por
// dezenas de arquivos. Perguntas banais de administração — *"o que a Tatiane
// enxerga?"*, *"quem entra no Financeiro?"* — exigiam ler código. Esta tela é a
// resposta, e ela **não tem nenhuma regra escrita dentro**: tudo vem de
// `GET /api/cadastro/mapa-acesso`, que lê o roteador do servidor em memória.
//
// ⚠️ Se um dia aparecer aqui uma lista de perfis ou módulos escrita à mão, ela
//    vai divergir do servidor e a tela passa a mentir sobre acesso — que é o
//    oposto do motivo de ela existir.
// ---------------------------------------------------------------------------

// Como cada rota é guardada. Os rótulos falam de CONSEQUÊNCIA, não de mecanismo:
// quem lê esta tela quer saber quem entra, não qual middleware está montado.
const ABERTURAS = {
    perfis:      { rotulo: 'Por perfil',        cor: '#0f766e', fundo: '#f0fdfa' },
    autenticado: { rotulo: 'Qualquer usuário',  cor: '#b45309', fundo: '#fffbeb' },
    // ⚠️ Guardada por `authorizeCapacidade`: quem entra sai da MATRIZ (padrão no
    //    código + exceções no banco), não de uma lista na rota. Sem esta etiqueta a
    //    rota apareceria como "qualquer usuário" — o mapa mentiria para MENOS
    //    protegido do que ela é.
    capacidade:  { rotulo: 'Pela matriz',       cor: '#0369a1', fundo: '#f0f9ff' },
    propria:     { rotulo: 'Regra do módulo',   cor: '#6d28d9', fundo: '#f5f3ff' },
    publico:     { rotulo: 'Sem login',         cor: '#be123c', fundo: '#fff1f2' },
};

const AdminAcessos = () => {
    const [mapa,      setMapa]      = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [erro,      setErro]      = useState('');
    // ⚠️ A MATRIZ É A VISÃO PADRÃO desde 18/09/2026, e é a resposta ao que a
    //    Julia relatou: o mapa derivado conta ROTAS, e ninguém decide acesso com
    //    "Gerente: financeiro 2 de 40 rotas". As duas visões antigas continuam
    //    aqui — elas cobrem os módulos que a matriz ainda não governa.
    const [visao,     setVisao]     = useState('matriz'); // 'matriz' | 'modulo' | 'perfil'
    const [busca,     setBusca]     = useState('');
    const [aberto,    setAberto]    = useState(null);     // módulo com o detalhe de rotas aberto

    const carregar = async () => {
        setCarregando(true);
        setErro('');
        try {
            const token = localStorage.getItem('authToken');
            const res = await fetch('/api/cadastro/mapa-acesso', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Erro ao carregar o mapa.');
            setMapa(data);
        } catch (e) {
            setErro(e.message || 'Erro de conexão.');
        } finally {
            setCarregando(false);
        }
    };

    useEffect(() => { carregar(); }, []);

    const termo = busca.trim().toLowerCase();

    const modulosFiltrados = useMemo(() => {
        if (!mapa?.modulos) return [];
        if (!termo) return mapa.modulos;
        // Busca por nome do módulo OU por perfil que o alcança — são as duas
        // maneiras naturais de procurar ("financeiro", "Diretoria RH").
        return mapa.modulos.filter(m =>
            m.modulo.includes(termo) || m.acessos.some(a => a.perfil.toLowerCase().includes(termo))
        );
    }, [mapa, termo]);

    const perfisFiltrados = useMemo(() => {
        if (!mapa?.perfis) return [];
        if (!termo) return mapa.perfis;
        return mapa.perfis.filter(p =>
            p.perfil.toLowerCase().includes(termo) || p.modulos.some(m => m.modulo.includes(termo))
        );
    }, [mapa, termo]);

    // ESTILOS
    const cartao = { backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0' };
    const rotuloFiltro = { fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '4px' };
    const th = { padding: '14px 20px', fontWeight: '600', borderBottom: '1px solid #e2e8f0', textAlign: 'left' };
    const td = { padding: '14px 20px', fontSize: '14px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' };

    const Etiqueta = ({ texto, cor, fundo, titulo }) => (
        <span title={titulo} style={{
            display: 'inline-block', padding: '3px 8px', borderRadius: '6px', fontSize: '11px',
            fontWeight: '700', backgroundColor: fundo, color: cor, whiteSpace: 'nowrap'
        }}>{texto}</span>
    );

    // Barra de cobertura: quantas rotas do módulo o perfil alcança.
    const Cobertura = ({ pct }) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '110px' }}>
            <div style={{ flex: 1, height: '6px', backgroundColor: '#f1f5f9', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: '100%', backgroundColor: pct === 100 ? '#0d9488' : '#64748b' }} />
            </div>
            <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '600', fontVariantNumeric: 'tabular-nums', minWidth: '32px', textAlign: 'right' }}>
                {pct}%
            </span>
        </div>
    );

    return (
        <div>
            {/* CABEÇALHO */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '15px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <div style={{ padding: '12px', backgroundColor: '#f5f3ff', borderRadius: '12px', color: '#8b5cf6' }}>
                        <ShieldCheck size={28} />
                    </div>
                    <div>
                        <h2 style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a', margin: '0 0 4px 0' }}>Mapa de Acesso</h2>
                        <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>Quem alcança o quê — lido do próprio servidor, não de uma lista.</p>
                    </div>
                </div>
                <button
                    onClick={carregar}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}
                >
                    <RefreshCw size={15} /> Atualizar
                </button>
            </div>

            {/* AS DUAS METADES DESTA ABA.
                A *matriz* é onde o acesso MUDA (por capacidade: card, aba, fila).
                O *mapa* é derivado do roteador e só de leitura — ele cobre o portal
                inteiro, inclusive os módulos que a matriz ainda não governa.
                ⚠️ As duas convivem de propósito: esconder o mapa faria a tela
                   mentir por omissão sobre os módulos ainda escritos no código. */}
            <div style={{ display: 'flex', gap: '6px', marginBottom: '20px', flexWrap: 'wrap' }}>
                {[
                    { id: 'matriz', rotulo: 'Matriz — conceder acesso', icone: SlidersHorizontal },
                    { id: 'modulo', rotulo: 'Mapa por módulo', icone: Layers },
                    { id: 'perfil', rotulo: 'Mapa por perfil', icone: UserCog },
                ].map(({ id, rotulo, icone: Icone }) => (
                    <button
                        key={id}
                        onClick={() => { setVisao(id); setAberto(null); }}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 16px', borderRadius: '8px',
                            fontSize: '13px', fontWeight: '700', cursor: 'pointer',
                            border: `1px solid ${visao === id ? '#8b5cf6' : '#e2e8f0'}`,
                            backgroundColor: visao === id ? '#f5f3ff' : '#fff',
                            color: visao === id ? '#6d28d9' : '#64748b',
                        }}
                    >
                        <Icone size={14} /> {rotulo}
                    </button>
                ))}
            </div>

            {visao === 'matriz' && <AdminAcessosMatriz />}

            {/* ⚠️ O aviso de mapa não montado é destaque, não rodapé: um mapa vazio
                se lê como "esses perfis não têm acesso" e levaria alguém a conceder
                permissão que a pessoa já tem. */}
            {visao !== 'matriz' && mapa && mapa.ok === false && (
                <div style={{ ...cartao, borderColor: '#fecdd3', backgroundColor: '#fff1f2', padding: '18px 20px', marginBottom: '20px', display: 'flex', gap: '12px' }}>
                    <AlertTriangle size={20} color="#be123c" style={{ flexShrink: 0 }} />
                    <div style={{ fontSize: '13.5px', color: '#9f1239', lineHeight: 1.55 }}>
                        <strong>O mapa não pôde ser montado.</strong> {mapa.aviso}
                    </div>
                </div>
            )}

            {visao !== 'matriz' && erro && (
                <div style={{ ...cartao, borderColor: '#fecdd3', backgroundColor: '#fff1f2', padding: '16px 20px', marginBottom: '20px', color: '#9f1239', fontSize: '13.5px' }}>
                    {erro}
                </div>
            )}

            {visao !== 'matriz' && carregando && (
                <div style={{ ...cartao, padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Lendo o roteador do servidor…</div>
            )}

            {visao !== 'matriz' && !carregando && mapa?.ok && (
                <>
                    {/* Como ler a tela. Sem isto, "Qualquer usuário" e "Regra do
                        módulo" seriam adivinhados — e adivinhar errado aqui vira
                        decisão errada de acesso. */}
                    <div style={{ ...cartao, padding: '18px 20px', marginBottom: '20px', backgroundColor: '#f8fafc' }}>
                        <div style={{ fontSize: '12px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase', marginBottom: '10px' }}>Como ler</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '18px', fontSize: '12.5px', color: '#475569' }}>
                            <span><Etiqueta texto="Por perfil" cor={ABERTURAS.perfis.cor} fundo={ABERTURAS.perfis.fundo} /> só os perfis da lista entram</span>
                            <span><Etiqueta texto="Qualquer usuário" cor={ABERTURAS.autenticado.cor} fundo={ABERTURAS.autenticado.fundo} /> exige login, mas <strong>não</strong> restringe perfil</span>
                            <span><Etiqueta texto="Pela matriz" cor={ABERTURAS.capacidade.cor} fundo={ABERTURAS.capacidade.fundo} /> por <strong>capacidade</strong> — quem entra se vê e se muda na matriz</span>
                            <span><Etiqueta texto="Regra do módulo" cor={ABERTURAS.propria.cor} fundo={ABERTURAS.propria.fundo} /> guarda própria, que o mapa não lê</span>
                            <span><Etiqueta texto="Sem login" cor={ABERTURAS.publico.cor} fundo={ABERTURAS.publico.fundo} /> aberta (link público, webhook)</span>
                        </div>
                        <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e2e8f0', fontSize: '12.5px', color: '#475569', lineHeight: 1.6 }}>
                            ⚠️ <strong>Alcançar um módulo não é ter acesso a tudo nele</strong> — daí a coluna de cobertura.
                            O <strong>Admin</strong> aparece como <em>bypass</em> porque entra em qualquer rota sem estar nas listas;
                            a exceção única do portal é o módulo <strong>Projetos</strong>, que autoriza por usuário e <strong>nega até o Admin</strong> —
                            por isso ele não aparece lá.
                        </div>
                    </div>

                    {/* CONTROLES */}
                    <div style={{ ...cartao, padding: '16px 20px', marginBottom: '20px', display: 'flex', gap: '20px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: '240px' }}>
                            <div style={rotuloFiltro}>Buscar</div>
                            <div style={{ position: 'relative' }}>
                                <Search size={15} color="#94a3b8" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                                <input
                                    value={busca}
                                    onChange={e => setBusca(e.target.value)}
                                    placeholder="módulo ou perfil…"
                                    style={{ width: '100%', padding: '9px 12px 9px 34px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }}
                                />
                            </div>
                        </div>
                        <div style={{ fontSize: '12.5px', color: '#64748b' }}>
                            <strong style={{ color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
                                {mapa.modulos.reduce((s, m) => s + m.totalRotas, 0)}
                            </strong> rotas · <strong style={{ color: '#0f172a' }}>{mapa.modulos.length}</strong> módulos
                        </div>
                    </div>

                    {/* ⚠️ Perfil citado nas rotas que não existe no catálogo: a tela de
                        troca de acesso NÃO consegue atribuí-lo. É defeito, não curiosidade —
                        foi assim que o perfil `Operacional`, dono do módulo BSV, apareceu. */}
                    {mapa.perfisForaDoCatalogo?.length > 0 && (
                        <div style={{ ...cartao, borderColor: '#fecdd3', backgroundColor: '#fff1f2', padding: '16px 20px', marginBottom: '20px', display: 'flex', gap: '10px' }}>
                            <AlertTriangle size={18} color="#be123c" style={{ flexShrink: 0, marginTop: '1px' }} />
                            <div style={{ fontSize: '13px', color: '#9f1239', lineHeight: 1.55 }}>
                                <strong>{mapa.perfisForaDoCatalogo.join(', ')}</strong> — {mapa.perfisForaDoCatalogo.length === 1 ? 'perfil citado' : 'perfis citados'} nas
                                rotas, mas fora do catálogo do portal. A tela de <strong>Usuários</strong> não consegue atribuir
                                {mapa.perfisForaDoCatalogo.length === 1 ? ' esse perfil' : ' esses perfis'} — acrescente ao <code>server/perfis.js</code>.
                            </div>
                        </div>
                    )}

                    {/* Informativo, não defeito: `Presidencia` é assim de propósito. */}
                    {mapa.perfisSemRota?.length > 0 && (
                        <div style={{ ...cartao, padding: '14px 20px', marginBottom: '20px', backgroundColor: '#f8fafc', fontSize: '12.5px', color: '#475569', lineHeight: 1.55 }}>
                            <strong>{mapa.perfisSemRota.join(', ')}</strong> {mapa.perfisSemRota.length === 1 ? 'existe no catálogo mas não é citado' : 'existem no catálogo mas não são citados'} por
                            nenhuma rota — {mapa.perfisSemRota.length === 1 ? 'toda rota protegida o nega' : 'toda rota protegida os nega'}. No caso de <strong>Presidencia</strong> isso é o desenho:
                            ele alcança só o módulo Projetos, por lista de usuário.
                        </div>
                    )}

                    {/* ⚠️ Router sem carimbo de módulo: nenhum perfil derivado entra
                        nele (falha fechada). Não é erro, mas precisa ser visível. */}
                    {mapa.semModulo?.length > 0 && (
                        <div style={{ ...cartao, borderColor: '#fde68a', backgroundColor: '#fffbeb', padding: '16px 20px', marginBottom: '20px' }}>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <AlertTriangle size={18} color="#d97706" style={{ flexShrink: 0, marginTop: '1px' }} />
                                <div style={{ fontSize: '13px', color: '#92400e', lineHeight: 1.55 }}>
                                    <strong>{mapa.semModulo.length} rota(s) sem módulo declarado.</strong> Nenhum perfil derivado
                                    (Diretoria RH, Diretoria Op, Marketing) alcança essas rotas — elas negam por padrão.
                                    <div style={{ marginTop: '8px', fontFamily: 'monospace', fontSize: '11.5px' }}>
                                        {mapa.semModulo.slice(0, 8).map(r => <div key={r.endereco + r.metodos}>{r.metodos.join(',')} {r.endereco}</div>)}
                                        {mapa.semModulo.length > 8 && <div>…e mais {mapa.semModulo.length - 8}</div>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* VISÃO POR MÓDULO */}
                    {visao === 'modulo' && (
                        <div style={{ ...cartao, overflow: 'hidden' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead style={{ backgroundColor: '#f8fafc', color: '#64748b', fontSize: '12px', textTransform: 'uppercase' }}>
                                    <tr>
                                        <th style={th}>Módulo</th>
                                        <th style={th}>Rotas</th>
                                        <th style={th}>Quem alcança</th>
                                        <th style={th}>Atenção</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {modulosFiltrados.length === 0 ? (
                                        <tr><td colSpan="4" style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Nada encontrado.</td></tr>
                                    ) : modulosFiltrados.map(m => (
                                        <React.Fragment key={m.modulo}>
                                            <tr>
                                                <td style={{ ...td, fontWeight: '700', color: '#0f172a', textTransform: 'capitalize' }}>
                                                    <button
                                                        onClick={() => setAberto(aberto === m.modulo ? null : m.modulo)}
                                                        style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: 'inherit' }}
                                                    >
                                                        <ChevronRight size={14} style={{ transform: aberto === m.modulo ? 'rotate(90deg)' : 'none', transition: 'transform .15s', color: '#94a3b8' }} />
                                                        {m.modulo}
                                                    </button>
                                                </td>
                                                <td style={{ ...td, fontVariantNumeric: 'tabular-nums', color: '#475569' }}>{m.totalRotas}</td>
                                                <td style={td}>
                                                    {m.acessos.length === 0 ? (
                                                        <span style={{ color: '#94a3b8', fontSize: '13px' }}>nenhum perfil — só a regra própria do módulo</span>
                                                    ) : (
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                            {m.acessos.map(a => (
                                                                <div key={a.perfil} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                    <span style={{ fontSize: '13px', color: '#0f172a', minWidth: '150px' }}>
                                                                        {a.perfil}
                                                                        {a.via === 'bypass' && (
                                                                            <span title="O Admin entra em qualquer rota sem estar nas listas. Não aparece onde o módulo tem regra própria — é o caso do Projetos, que nega até o Admin."
                                                                                  style={{ marginLeft: '6px', fontSize: '10.5px', fontWeight: '700', color: '#334155', backgroundColor: '#f1f5f9', padding: '2px 6px', borderRadius: '5px' }}>
                                                                                bypass
                                                                            </span>
                                                                        )}
                                                                        {a.via === 'herança' && (
                                                                            <span title={`Não está na lista das rotas: entra por herdar de ${a.herdaDe}, e só nos módulos do recorte dele.`}
                                                                                  style={{ marginLeft: '6px', fontSize: '10.5px', fontWeight: '700', color: '#6d28d9', backgroundColor: '#f5f3ff', padding: '2px 6px', borderRadius: '5px' }}>
                                                                                herda de {a.herdaDe}
                                                                            </span>
                                                                        )}
                                                                    </span>
                                                                    <Cobertura pct={a.cobertura} />
                                                                    <span style={{ fontSize: '11.5px', color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>{a.rotas}/{m.totalRotas}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </td>
                                                <td style={td}>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', alignItems: 'flex-start' }}>
                                                        {m.rotasAutenticado > 0 && <Etiqueta texto={`${m.rotasAutenticado} qualquer usuário`} cor={ABERTURAS.autenticado.cor} fundo={ABERTURAS.autenticado.fundo} titulo="Rotas que exigem login mas não restringem perfil" />}
                                                        {m.rotasCapacidade > 0 && <Etiqueta texto={`${m.rotasCapacidade} pela matriz`} cor={ABERTURAS.capacidade.cor} fundo={ABERTURAS.capacidade.fundo} titulo="Guardadas por capacidade — quem entra se muda na matriz, sem deploy" />}
                                                        {m.rotasPropria > 0 && <Etiqueta texto={`${m.rotasPropria} regra do módulo`} cor={ABERTURAS.propria.cor} fundo={ABERTURAS.propria.fundo} titulo="Guardadas por regra própria, que o mapa não sabe ler" />}
                                                        {m.rotasPublicas > 0 && <Etiqueta texto={`${m.rotasPublicas} sem login`} cor={ABERTURAS.publico.cor} fundo={ABERTURAS.publico.fundo} titulo="Abertas: link público, webhook" />}
                                                    </div>
                                                </td>
                                            </tr>
                                            {aberto === m.modulo && (
                                                <tr>
                                                    <td colSpan="4" style={{ padding: '0 20px 20px 20px', backgroundColor: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                                                        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '12px' }}>
                                                            <tbody>
                                                                {m.rotas.map(r => (
                                                                    <tr key={r.endereco + r.metodos.join()}>
                                                                        <td style={{ padding: '7px 10px', fontSize: '11.5px', fontWeight: '700', color: '#64748b', width: '70px', fontFamily: 'monospace' }}>{r.metodos.join(',')}</td>
                                                                        <td style={{ padding: '7px 10px', fontSize: '12px', fontFamily: 'monospace', color: '#0f172a' }}>{r.endereco}</td>
                                                                        <td style={{ padding: '7px 10px', width: '150px' }}>
                                                                            <Etiqueta texto={ABERTURAS[r.abertura]?.rotulo || r.abertura} cor={ABERTURAS[r.abertura]?.cor} fundo={ABERTURAS[r.abertura]?.fundo} />
                                                                        </td>
                                                                        <td style={{ padding: '7px 10px', fontSize: '12px', color: '#475569' }}>{r.perfis.join(', ') || '—'}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* VISÃO POR PERFIL */}
                    {visao === 'perfil' && (
                        <div style={{ ...cartao, overflow: 'hidden' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead style={{ backgroundColor: '#f8fafc', color: '#64748b', fontSize: '12px', textTransform: 'uppercase' }}>
                                    <tr>
                                        <th style={th}>Perfil</th>
                                        <th style={th}>Módulos</th>
                                        <th style={th}>O que alcança</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {perfisFiltrados.length === 0 ? (
                                        <tr><td colSpan="3" style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Nada encontrado.</td></tr>
                                    ) : perfisFiltrados.map(p => (
                                        <tr key={p.perfil}>
                                            <td style={{ ...td, fontWeight: '700', color: '#0f172a' }}>{p.perfil}</td>
                                            <td style={{ ...td, fontVariantNumeric: 'tabular-nums', color: '#475569' }}>{p.modulos.length}</td>
                                            <td style={td}>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                    {p.modulos.map(m => (
                                                        <div key={m.modulo} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                            <span style={{ fontSize: '13px', color: '#0f172a', minWidth: '150px', textTransform: 'capitalize' }}>
                                                                {m.modulo}
                                                                {m.via === 'bypass' && (
                                                                    <span title="O Admin entra em qualquer rota sem estar nas listas."
                                                                          style={{ marginLeft: '6px', fontSize: '10.5px', fontWeight: '700', color: '#334155', backgroundColor: '#f1f5f9', padding: '2px 6px', borderRadius: '5px' }}>
                                                                        bypass
                                                                    </span>
                                                                )}
                                                                {m.via === 'herança' && (
                                                                    <span title={`Entra por herdar de ${m.herdaDe}, e só nos módulos do recorte dele.`}
                                                                          style={{ marginLeft: '6px', fontSize: '10.5px', fontWeight: '700', color: '#6d28d9', backgroundColor: '#f5f3ff', padding: '2px 6px', borderRadius: '5px' }}>
                                                                        herança
                                                                    </span>
                                                                )}
                                                            </span>
                                                            <Cobertura pct={m.cobertura} />
                                                            <span style={{ fontSize: '11.5px', color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>{m.rotas}/{m.totalRotas}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default AdminAcessos;
